import * as React from "react";
import {useEffect, useMemo, useState} from "react";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import {
    ACCOUNT_NUMBER_REGEX,
    INTERACTION_PROCESS_MTA_CHANGE_DIRECT_DEBIT,
    Mandate,
    Policy,
    SORT_CODE_REGEX
} from "@ferry/journey-services";
import {lazyInject} from "@athena-portal/core";
import {Checkbox} from "../../../../../components/inputs/Checkbox";
import {DirectDebitGuarantee} from "../../../../../components/directDebit/DirectDebitGuarantee";
import {Button, Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import {PageTitle} from "../../../../../components/navigation/PageTitle";
import {getInputClassName} from "../../../../../CommonFunctions";
import {TextInputWithoutLabel} from "../../../../../components/inputs/TextInputWithoutLabel";
import {IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {AjaxError} from "rxjs/ajax";
import {WarningModal} from "../../../../../components/modals/WarningModal";
import {Types} from "../../../../../Types";
import type {ICustomerPortalService} from "../../../../../services/ICustomerPortalService";
import {Link} from "react-router-dom";
import {formatSortCode, SortCodeInput, resourcesHelper, Footer} from "@ferry/portal-core";

const DASHBOARD_PATH = "/dashboard";
const ACCOUNT_NUMBER_FIELD_ID = "accountNumber";
const ACCOUNT_SORT_CODE_FIELD_ID = "accountSortCode";
const CONFIRM_DATA_FIELD_ID = "confirmDataCheckbox";

interface IParams {
    policyNumber: string;

    [key: string]: string | undefined
}

interface IDirectDebitAddPagePlainProps extends IWithRouterProps<IParams> {
    customerPortalService: ICustomerPortalService;
}

const getPolicyHolderAccountName = (policy: Policy): string => {
    if (!policy.policyHolder) {
        return "";
    }

    return [policy.policyHolder.firstName, policy.policyHolder.lastName]
        .filter(Boolean)
        .join(" ");
}

const copyMandate = (mandate: Mandate, values: Partial<Mandate>): Mandate =>
    Object.assign(new Mandate(), mandate, values);

export const DirectDebitAddPagePlain = ({customerPortalService, params, navigate}: IDirectDebitAddPagePlainProps) => {
    const pluginTemplates = useMemo(() => new PluginTemplates(), []);
    const policyNumber = params.policyNumber;

    const [newMandate, setNewMandate] = useState<Mandate>(new Mandate());
    const [sortCodePartOne, setSortCodePartOne] = useState("");
    const [sortCodePartTwo, setSortCodePartTwo] = useState("");
    const [sortCodePartThree, setSortCodePartThree] = useState("");
    const [confirmDataChecked, setConfirmDataChecked] = useState<boolean | undefined>();
    const [accountNumberIsValid, setAccountNumberIsValid] = useState<boolean | undefined>();
    const [sortCodeIsValid, setSortCodeIsValid] = useState<boolean | undefined>();
    const [showBankValidationFailed, setShowBankValidationFailed] = useState(false);
    const [showOverlay, setShowOverlay] = useState(false);
    const [modalOpened, setModalOpened] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        pluginTemplates.setPageTitle("Add your direct debit details");

        const subscription = customerPortalService
            .getPolicy(policyNumber)
            .subscribe((policy: Policy) => {
                setNewMandate(mandate => copyMandate(mandate, {
                    accountName: getPolicyHolderAccountName(policy)
                }));
            });

        return () => subscription.unsubscribe();
    }, [customerPortalService, pluginTemplates, policyNumber]);


    const isAccountDetailsSet = (): boolean => {
        return ACCOUNT_NUMBER_REGEX.test(newMandate.accountNumber)
            && SORT_CODE_REGEX.test(newMandate.sortCode)
            && !!confirmDataChecked;
    }

    const toggle = (): void => {
        setModalOpened(opened => !opened);
    }

    const toggleAndRemoveFlag = (): void => {
        setModalOpened(opened => !opened);
        setConfirmDataChecked(false);
    }

    const handleAccountNumber = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const accountNumber = event.target.value.replace(/\D/g, '');
        const accountNumberValid = ACCOUNT_NUMBER_REGEX.test(accountNumber);


        setNewMandate(mandate => copyMandate(mandate, {accountNumber}));
        setAccountNumberIsValid(accountNumberValid);
    }

    const focusInput = (inputId: string, currentValue: string): void => {
        if (currentValue.length === 2) {
            const input = document.getElementById(inputId);
            input?.focus();
        }
    }

    const handleSortCodePartOne = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const code = event.target.value.replace(/\D/g, '').slice(0, 2);
        const sortCode = code + sortCodePartTwo + sortCodePartThree;

        setNewMandate(mandate => copyMandate(mandate, {sortCode}));
        setSortCodePartOne(code);
        setSortCodeIsValid(undefined);
        focusInput("sortCodeInputSecondPart", code);
    }

    const handleSortCodePartTwo = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const code = event.target.value.replace(/\D/g, '').slice(0, 2);
        const sortCode = sortCodePartOne + code + sortCodePartThree;

        setNewMandate(mandate => copyMandate(mandate, {sortCode}));
        setSortCodePartTwo(code);
        setSortCodeIsValid(undefined);
        focusInput("sortCodeInputThirdPart", code);
    }

    const handleSortCodePartThree = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const code = event.target.value.replace(/\D/g, '').slice(0, 2);
        const sortCode = sortCodePartOne + sortCodePartTwo + code;

        setNewMandate(mandate => copyMandate(mandate, {sortCode}));
        setSortCodePartThree(code);
        setSortCodeIsValid(undefined);
    }

    const closeBankValidationFailModal = (): void => {
        setShowBankValidationFailed(value => !value);
    }

    const mergeSortCodeEvent = (): void => {
        setNewMandate(mandate => copyMandate(mandate, {
            sortCode: sortCodePartOne + sortCodePartTwo + sortCodePartThree
        }));
    }

    const handleConfirmData = (value: boolean): void => {

        setConfirmDataChecked(value);
        setModalOpened(value);
    }

    const scrollToElement = (fields: string[]): void => {
        if (fields.length > 0) {
            const firstElement = fields[0];
            const element = document.getElementById(firstElement);
            if (element !== null) {
                scrollTo(element.getBoundingClientRect().left, element.getBoundingClientRect().top + window.scrollY);
            }
        }
    }

    const submitChanges = (): void => {
        setShowOverlay(true);
        customerPortalService.createDirectDebit(policyNumber, newMandate)
            .subscribe(
                (response: any) => {
                    if (response instanceof AjaxError) {
                        if (response.status === 409) {
                            setShowBankValidationFailed(true);
                            setAccountNumberIsValid(false);
                            setSortCodeIsValid(false);
                            setShowOverlay(false);
                        }
                    } else if (!response.valid && response.ddUnsupported) {
                        setShowBankValidationFailed(true);
                        setAccountNumberIsValid(false);
                        setSortCodeIsValid(false);
                        setShowOverlay(false);
                    } else {
                        window.sessionStorage.setItem("showPopup", "true");
                        window.sessionStorage.setItem("referrerJourney", INTERACTION_PROCESS_MTA_CHANGE_DIRECT_DEBIT);
                        navigate(DASHBOARD_PATH);
                        setShowOverlay(false);
                        setShowBankValidationFailed(false);
                    }
                }, () => {
                    setShowOverlay(false);
                });
    }

    const process = (nextAccountNumberIsValid: boolean, nextSortCodeIsValid: boolean, nextConfirmDataChecked: boolean, invalidFields: string[]): void => {
        const isFormValid = nextAccountNumberIsValid
            && nextSortCodeIsValid
            && nextConfirmDataChecked;

        if (isFormValid) {
            submitChanges();
        } else {
            scrollToElement(invalidFields);
        }
    }

    const handleForm = (): void => {
        const invalidFields: string[] = [];
        const checkBoxChecked = !!confirmDataChecked;

        if (!accountNumberIsValid) {
            invalidFields.push(ACCOUNT_NUMBER_FIELD_ID);
        }
        if (!sortCodeIsValid) {
            invalidFields.push(ACCOUNT_SORT_CODE_FIELD_ID);
        }
        if (!confirmDataChecked) {
            invalidFields.push(CONFIRM_DATA_FIELD_ID);
        }

        const firstInvalidFields = invalidFields.slice(0, 1);
        const nextAccountNumberIsValid = newMandate.accountNumber ? ACCOUNT_NUMBER_REGEX.test(newMandate.accountNumber) : false;
        const nextSortCodeIsValid = newMandate.sortCode ? SORT_CODE_REGEX.test(newMandate.sortCode) : false;

        setAccountNumberIsValid(nextAccountNumberIsValid);
        setSortCodeIsValid(nextSortCodeIsValid);
        setConfirmDataChecked(checkBoxChecked);

        process(nextAccountNumberIsValid, nextSortCodeIsValid, checkBoxChecked, firstInvalidFields);
    }

    const handleMoveBack = (): void => {
        navigate(DASHBOARD_PATH);
    }

    const renderIntro = (): React.ReactNode => (
        <div className="mb-3 p-0">
            <p className="font-weight-bold">
                To be able to process a refund to your bank account we need to set up a direct
                debit but don’t worry no payments will be taken and once you have received your
                refund you can cancel the direct debit with your bank</p>
            <p>Need help? <Link className="d-inline font-weight-bold text-decoration-underline"
                                target="_blank" to={`/dashboard/help/${policyNumber}`}>
                Our FAQs
            </Link> have the answers for Direct Debit queries.
            </p>
        </div>
    );

    const renderAccountHolder = (): React.ReactNode => (
        <div className="pt-4">
            <p className="font-weight-semi-bold mb-3">Name of account holder</p>
            <p>{newMandate.accountName}</p>
        </div>
    );

    const renderAccountNumber = (): React.ReactNode => (
        <div className="form-group mb-0">
            <div className="justify-content-between align-items-start">
                <div>
                    <label className="mb-3" id={ACCOUNT_NUMBER_FIELD_ID} htmlFor="accountNumberInput">
                        Account number
                    </label>
                    <div className="d-flex">
                        <TextInputWithoutLabel
                            id="accountNumberInput"
                            value={newMandate.accountNumber}
                            onChange={handleAccountNumber}
                            minLength={8}
                            maxLength={8}
                            className={getInputClassName(accountNumberIsValid) + " account-number-input-width"}
                            inputValid={accountNumberIsValid}
                            validationMessage={newMandate?.accountNumber ? "The account number you've entered is incorrect." : "Please answer this to carry on"}
                        />
                    </div>
                    <div className="pb-2">
                        <p className="font-weight-light"> This is normally found on your debit card.
                            It is a sequence of 8 digits.</p>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderSortCodeValidationMessage = (): React.ReactNode => {
        if (!newMandate?.sortCode) {
            return <div className="invalid-feedback d-block">Please answer this to carry on</div>;
        }

        const validationMessage = 'The bank details you\'ve entered are incorrect. Please check the sort code and account number provided.';

        return <div className="invalid-feedback d-block">{validationMessage}</div>;
    }


    const renderConfirmationCheckbox = (): React.ReactNode => (
        <>
            <Checkbox
                label={<span className={"font-weight-600"}>I confirm, the name of the account holder,
                    account number and sort code are correct.
                    I am also the only person authorised to set up Direct Debits from this account.</span>}
                name={CONFIRM_DATA_FIELD_ID}
                id={CONFIRM_DATA_FIELD_ID}
                value={!!confirmDataChecked}
                className={"mt-4"}
                onChanged={handleConfirmData}/>
            {confirmDataChecked === false &&
                <div className="invalid-feedback d-block">Please answer this to carry on</div>}
        </>
    );

    const renderPositiveModal = (): React.ReactNode => {
        const closeBtn = <button className="btn-close" onClick={toggleAndRemoveFlag}/>

        return (
            <Modal className="my-2 info-modal" isOpen={modalOpened} toggle={toggleAndRemoveFlag}>
                <ModalHeader toggle={toggleAndRemoveFlag} close={closeBtn}>
                    <div className="info-popup-title">
                        <img src={`/${resourcesHelper.THEME_PATH}/img/info-popup.svg`} alt="Attention"/>
                        <div className="text-blue-2 font-weight-medium">Please confirm your bank account details are
                            correct
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody className="pt-0">
                    <p className="mb-0">Name of account holder: <span
                        className="font-weight-bold">{newMandate.accountName}</span></p>
                    <p className="mb-0">Account number: <span
                        className="font-weight-bold">{newMandate.accountNumber}</span></p>
                    <p className="mb-0">Sort code: <span
                        className="font-weight-bold">{formatSortCode(newMandate.sortCode)}</span></p>
                    <p className="font-weight-bold">Are the above bank account details correct?</p>
                </ModalBody>
                <ModalFooter className="d-flex flex-column modal-footer">
                    <div className="row">
                        <button type="button" className="btn-white width-md-250" onClick={toggleAndRemoveFlag}>No,
                            amend my details
                        </button>
                        <button type="button" className="btn-primary width-md-250 mb-1 btn-next-no-arrow"
                                onClick={toggle}>Yes, my details are correct
                        </button>
                    </div>
                </ModalFooter>
            </Modal>
        );
    }

    const renderNegativeModal = (): React.ReactNode => {
        const closeBtn = <button className="btn-close" onClick={toggleAndRemoveFlag}/>

        return (
            <Modal className="my-2 info-modal" isOpen={modalOpened} toggle={toggleAndRemoveFlag}>
                <ModalHeader toggle={toggleAndRemoveFlag} close={closeBtn}>
                    <div className="d-flex">
                        <img className="d-block mr-3 mb-1 info-button-width"
                             src={`/${resourcesHelper.THEME_PATH}/img/alert-popup.svg`} alt="Warning icon"/>
                        <div className="text-blue-2 font-weight-medium">Please check your bank account details</div>
                    </div>
                </ModalHeader>
                <ModalBody className="pt-0 font-weight-bold">
                    <p>The bank account details you've entered are incomplete or incorrect. Please check them and try
                        again.</p>
                </ModalBody>
                <ModalFooter className="d-flex flex-column modal-footer align-self-end">
                    <button type="button" className="btn btn-primary width-md-250 mb-1 btn-next-no-arrow"
                            onClick={toggleAndRemoveFlag}>Ok, I understand
                    </button>
                </ModalFooter>
            </Modal>
        );
    }

    const renderConfirmationModal = (): React.ReactNode =>
        isAccountDetailsSet() ? renderPositiveModal() : renderNegativeModal();

    const renderBankValidationFailedModal = (): React.ReactNode => {
        if (!showBankValidationFailed) {
            return null;
        }

        return (
            <WarningModal
                isOpen={showBankValidationFailed}
                onModalHide={closeBankValidationFailModal}
                id={"bankValidationFailModal"}
                title={"The bank details you’ve entered are incorrect."}
                body={<p>Please check the sort code and account number provided.</p>}/>
        );
    }

    const renderAccount = (): React.ReactNode => (
        <React.Fragment>
            {renderAccountHolder()}
            {renderAccountNumber()}
            <SortCodeInput
                labelId={ACCOUNT_SORT_CODE_FIELD_ID}
                sortCodePartOne={sortCodePartOne}
                sortCodePartTwo={sortCodePartTwo}
                sortCodePartThree={sortCodePartThree}
                sortCodeIsValid={sortCodeIsValid}
                validationMessage={renderSortCodeValidationMessage()}
                onPartOneChange={handleSortCodePartOne}
                onPartTwoChange={handleSortCodePartTwo}
                onPartThreeChange={handleSortCodePartThree}
                onBlur={mergeSortCodeEvent}/>
            {renderConfirmationCheckbox()}
            {renderConfirmationModal()}
        </React.Fragment>
    );

    return (
        <>
            <div className={"container-fluid"}>
                <div className={"container pt-3 pb-3"}>
                    <FalconHeader/>
                    <div className="plugin">
                        {pluginTemplates.renderBackReloadLink("Back", true, DASHBOARD_PATH)}
                        <PageTitle title={"Set up Direct Debit"}
                                   className={"text-group title plugin no-indent-desktop"}/>
                        <div className="panel bg-white overflow-hidden">
                            {renderIntro()}
                            {renderAccount()}
                            <DirectDebitGuarantee/>
                        </div>
                        <Button className={"btn btn-primary btn-next-no-arrow btn-wide"}
                                type={"button"}
                                onClick={handleForm}>Save Direct Debit details
                        </Button>
                        <Button type="button" className="btn btn-back" onClick={handleMoveBack}>Cancel
                            changes</Button>
                        <div>
                            {pluginTemplates.renderBackReloadLink("Back", false, DASHBOARD_PATH)}
                        </div>
                    </div>
                    <Footer/>
                </div>
            </div>
            <Overlay show={showOverlay}/>
            {renderBankValidationFailedModal()}
        </>
    );
}

class DirectDebitAddPageContainer extends React.Component<IWithRouterProps<IParams>> {

    @lazyInject(Types.CustomerPortalService)
    private readonly customerPortalService: ICustomerPortalService;

    public render(): React.ReactNode {
        return <DirectDebitAddPagePlain {...this.props} customerPortalService={this.customerPortalService}/>;
    }
}

export const DirectDebitAddPage = withRouter<{}, IParams>(DirectDebitAddPageContainer)
